/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { services } from "../../services";
import { actions } from "../../actions";

import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/use-toast";
// import Loader from '../loading';
import CustomLoader from "../ui/custom-loader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { FileText, Eye } from "lucide-react";
import {
  ApproveUserProps,
  KYCContract,
  ComplianceContract,
  ApiResponse,
} from "../../types/users/approve.user";
import { AxiosError } from "axios";

const ApproveUser: React.FC<ApproveUserProps> = ({ record }) => {
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [status, setStatus] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [kycContract, setKYCContract] = useState<KYCContract | null>(null);
  const [complianceContract, setComplianceContract] =
    useState<ComplianceContract | null>(null);
  const [adminRoleHash, setAdminRoleHash] = useState<string>("");
  const [web3Data, setWeb3Data] = useState<any>(null);
  const [callAPI, setCallAPI] = useState<boolean>(false);
  const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function initializeContracts() {
      try {
        const web3Data = await services.getWeb3();
        setWeb3Data(web3Data);

        const kycInstance = await actions.getKycVerificationContract();
        const complianceInstance =
          await actions.getComplianceRegistryContract();

        setKYCContract(kycInstance as unknown as KYCContract);
        setComplianceContract(
          complianceInstance as unknown as ComplianceContract,
        );

        const adminRoleHash = await kycInstance.methods.ADMIN_ROLE().call();
        if (typeof adminRoleHash === "string") {
          setAdminRoleHash(adminRoleHash);
        }
      } catch (error) {
        toast({
          description: "Error initializing contracts",
          variant: "destructive",
        });
      }
    }

    initializeContracts();
  }, []);

  const handleApprove = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    try {
      const user = [
        {
          id: record.id,
          isComplianceVerified: true,
          isKycVerified: true,
          status: true,
        },
      ];
      const params = {
        user: user,
        complianceHash: transactionId,
        kycHash: transactionId,
      };

      const response = (await services.post(
        "/user/approveInvestor",
        params,
      )) as ApiResponse;

      if (response?.status === 200) {
        setLoading(false);
        toast({
          description: response.data?.message || "User approved successfully",
          variant: "default",
        });
        setStatus(true);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setLoading(false);
        throw new Error("Failed to approve user");
      }
    } catch (err) {
      const error = err as AxiosError;

      if (error?.response) {
        if (error?.response.status === 401 || error?.response?.status === 403) {
          toast({
            description: "Unauthorized Request",
            variant: "destructive",
          });
          localStorage.removeItem("token");
        }
      } else {
        toast({ description: "Request failed", variant: "destructive" });
      }
      toast({ description: "Error approving user", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [loading, record.id, transactionId]);

  useEffect(() => {
    if (callAPI) {
      handleApprove();
    }
  }, [callAPI, handleApprove]);

  const onTransaction = async (walletAddress: string): Promise<boolean> => {
    setLoading(true);
    if (!web3Data || !kycContract || !complianceContract || !adminRoleHash) {
      toast({
        description: "Contract not initialized properly",
        variant: "destructive",
      });
      return false;
    }

    try {
      const hasAdminRole = await kycContract.methods
        .hasRole(adminRoleHash, web3Data.accounts[0])
        .call();

      if (!hasAdminRole) {
        toast({ description: "Current wallet does not have admin role" });
        navigate("/users");
        return false;
      }

      const metadata = `Approved by admin: ${web3Data.accounts[0]} on ${new Date().toISOString()}`;
      const country = record?.country || "Unknown";
      const kycStatus = 2;

      await kycContract.methods
        .setVerificationData(walletAddress, metadata, country, kycStatus)
        .send({ from: web3Data.accounts[0] })
        .on("transactionHash", (hash: string) => {
          setTransactionId(hash); // Log the transaction hash t
        })
        .on("receipt", async () => {
          try {
            const complianceMetadata = `Compliance approved by admin: ${web3Data.accounts[0]} on ${new Date().toISOString()}`;

            await complianceContract.methods
              .updateComplianceStatus(walletAddress, true, complianceMetadata)
              .send({ from: web3Data.accounts[0] })
              .then(() => {
                setLoading(false);
                setCallAPI(true);
                toast({
                  description: "Compliance status updated successfully",
                  variant: "default",
                });
              })
              .catch((error: Error) => {
                setLoading(false);
                toast({
                  description: "Failed to update compliance status",
                  variant: "destructive",
                });
              });
          } catch (complianceError) {
            toast({
              description: "Compliance update failed",
              variant: "destructive",
            });
          }
        })
        .on("error", (error: Error) => {
          toast({
            description: "KYC verification failed",
            variant: "destructive",
          });
        });

      return true;
    } catch (error: any) {
      setLoading(false);

      toast({
        description: "Transaction failed: " + (error.message || error),
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center w-full">
        <CustomLoader size="md" />
      </div>
    );
  }

  // Only show approve button if user status is pending (accept any casing: pending, PENDING, Pending)
  if (record.status?.toLowerCase() !== "pending") {
    return null;
  }

  const isPdf =
    record.governmentId?.toLowerCase().endsWith(".pdf") ||
    record.governmentId?.includes(".pdf") ||
    record.governmentId?.toLowerCase().includes("application/pdf");

  return (
    <>
      <div className="flex gap-2 items-center justify-center">
        {record.governmentId && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDocumentModal(true)}
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            View ID
          </Button>
        )}
        <Button
          className="bg-gradientButton hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md"
          onClick={() => onTransaction(record.walletAddress)}
          disabled={loading}
        >
          Approve
        </Button>
      </div>

      {/* Document View Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Government ID Document
            </DialogTitle>
            <DialogDescription>
              Review the user's government ID before approval
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 max-h-[70vh] overflow-auto">
            {record.governmentId ? (
              <div className="flex flex-col items-center gap-4">
                {isPdf ? (
                  <iframe
                    src={record.governmentId}
                    className="w-full h-[600px] border border-gray-300 rounded-lg"
                    title="Government ID Document"
                  />
                ) : (
                  <img
                    src={record.governmentId}
                    alt="Government ID"
                    className="max-w-full h-auto rounded-lg border border-gray-300"
                    style={{ maxHeight: "600px" }}
                  />
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => window.open(record.governmentId, "_blank")}
                  >
                    Open in New Tab
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDocumentModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No Government ID document available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApproveUser;
